// ============================================================
// Permissions Catalog
// Single source of truth for the RBAC permission keys used by
// backend (Zod validation, middleware) and frontend (role editor,
// nav filter). Admins implicitly receive every permission.
// ============================================================

export interface PermissionGroupDefinition {
  key: string;
  label: string;
  description: string;
  view: string;
  manage?: string;
  /**
   * Sensible Berechtigung (Gesundheitsdaten, Art. 9 DSGVO).
   * Wird NICHT implizit an Admins vergeben (Need-to-know) und muss explizit
   * einer Rolle zugewiesen werden. Durchsetzung über requireMedicalAccess.
   */
  sensitive?: boolean;
}

export const PERMISSION_GROUPS: PermissionGroupDefinition[] = [
  {
    key: 'persons',
    label: 'Stammbaum & Personen',
    description: 'Personen, Beziehungen und der Stammbaum selbst.',
    view: 'persons.view',
    manage: 'persons.manage',
  },
  {
    key: 'archive',
    label: 'Archiv',
    description: 'Medien, Dokumente, Fotos im Archiv.',
    view: 'archive.view',
    manage: 'archive.manage',
  },
  {
    key: 'albums',
    label: 'Alben',
    description: 'Alben anlegen und Medien zuordnen.',
    view: 'albums.view',
    manage: 'albums.manage',
  },
  {
    key: 'statistics',
    label: 'Statistiken',
    description: 'Aggregierte Auswertungen über die Familie.',
    view: 'statistics.view',
  },
  {
    key: 'lifelines',
    label: 'Lebenslinien',
    description: 'Zeitstrahl-Visualisierung der Lebensspannen.',
    view: 'lifelines.view',
  },
  {
    key: 'calendar',
    label: 'Kalender',
    description: 'Geburts- und Todestage im Kalender.',
    view: 'calendar.view',
  },
  {
    key: 'cosmos',
    label: 'Sternenhimmel',
    description: 'Cosmos-Visualisierung der Familie.',
    view: 'cosmos.view',
  },
  {
    key: 'search',
    label: 'Suche',
    description: 'Volltextsuche über Personen und Daten.',
    view: 'search.view',
  },
  {
    key: 'relationshipFinder',
    label: 'Pfadfinder',
    description: 'Verwandtschaftsbeziehungen ermitteln.',
    view: 'relationshipFinder.view',
  },
  {
    key: 'coldCases',
    label: 'Cold Cases',
    description: 'Offene Forschungsfragen und Fälle.',
    view: 'coldCases.view',
    manage: 'coldCases.manage',
  },
  {
    key: 'fogOfWar',
    label: 'Entdecker-Karte',
    description: 'Geografische Entdeckungs-Visualisierung.',
    view: 'fogOfWar.view',
    manage: 'fogOfWar.manage',
  },
  {
    key: 'quiz',
    label: 'Familien-Quiz',
    description: 'Quiz mit Familienfragen spielen.',
    view: 'quiz.view',
  },
  {
    key: 'achievements',
    label: 'Trophäenraum',
    description: 'Erfolge und Auszeichnungen einsehen.',
    view: 'achievements.view',
  },
  {
    key: 'medical',
    label: 'Medizin & Vererbung',
    description:
      'Krankheiten, Todesursachen und phänotypische Merkmale. Sensible Gesundheitsdaten (Art. 9 DSGVO) – wird nicht automatisch an Admins vergeben.',
    view: 'medical.view',
    manage: 'medical.manage',
    sensitive: true,
  },
  {
    key: 'medicalSensitive',
    label: 'Erbkrankheiten & Risiko',
    description:
      'Erbkrankheiten, Vererbungsmuster und berechnete Trägerschafts-/Risiko-Auswertungen. Höchste Schutzstufe.',
    view: 'medicalSensitive.view',
    manage: 'medicalSensitive.manage',
    sensitive: true,
  },
];

export const ALL_PERMISSIONS: string[] = PERMISSION_GROUPS.flatMap((g) =>
  g.manage ? [g.view, g.manage] : [g.view],
);

export const PERMISSION_SET: ReadonlySet<string> = new Set(ALL_PERMISSIONS);

/**
 * Sensible Berechtigungen (Gesundheitsdaten). Werden Admins NICHT implizit
 * gewährt – die Durchsetzung erfolgt über requireMedicalAccess (kein Bypass).
 */
export const SENSITIVE_PERMISSIONS: ReadonlySet<string> = new Set(
  PERMISSION_GROUPS.filter((g) => g.sensitive).flatMap((g) =>
    g.manage ? [g.view, g.manage] : [g.view],
  ),
);

export function isValidPermission(key: string): boolean {
  return PERMISSION_SET.has(key);
}

export function isSensitivePermission(key: string): boolean {
  return SENSITIVE_PERMISSIONS.has(key);
}
